/**
 * The panel shell — every section of the console wears it.
 *
 * It owns three things no panel should decide for itself:
 *
 *   **Loading is never empty.** No answer yet draws a skeleton at the panel's final size; an answer
 *   that says there is nothing draws that. They are different claims.
 *
 *   **Trouble is a sentence, and Retry is the second attempt.** The first failure renders the
 *   browser's sentence; *Retry* makes the second; after that the button is gone, because there is
 *   no third and a button that does nothing is worse than none.
 *
 *   **A dropped stream greys the panel**: what it shows is the last thing the server said, and the
 *   header says the stream is reconnecting.
 */

import type { ReactNode } from "react";
import { useCompetition, type Slot } from "../../store/competition";
import { PanelBoundary } from "./PanelBoundary";
import { Info } from "./Info";
import { useT } from "../../i18n/useT";

interface Props<T> {
  /** The `data-panel` the e2e and the reader both look for. */
  name: string;
  title: ReactNode;
  slot: Slot<T>;
  /** Which read a Retry repeats. */
  retry: "today" | "portfolio" | "skills";
  /** Roughly how many lines the panel will be, so its skeleton is its own size. */
  lines?: number;
  /** The ⓘ definition beside the title, for a teammate who doesn't know the term. */
  info?: string;
  /** A short fact that belongs with the title itself, e.g. the run's times and cost. */
  meta?: ReactNode;
  /** The heading's level: Today is the page's `h1`, the rest are `h2`. */
  h1?: boolean;
  children: (data: T) => ReactNode;
}

export function Panel<T>({ name, title, slot, retry, lines = 4, info, meta, h1, children }: Props<T>) {
  const { t } = useT();
  const again = useCompetition((s) => s.retry);
  const status = useCompetition((s) => s.stream.status);
  const dropped = (status === "reconnecting" || status === "unreachable") && slot.data !== null;
  const Heading = h1 ? "h1" : "h2";

  return (
    <PanelBoundary panelKey={name} title={typeof title === "string" ? title : name}>
      <section className="panel" data-panel={name} aria-busy={slot.data === null && !slot.says}>
        <header className="panel__head">
          <Heading className="panel__title">
            <span>{title}</span>
            {info ? <Info term={typeof title === "string" ? title : name} text={info} /> : null}
          </Heading>
          {meta ? <span className="panel__meta">· {meta}</span> : null}
        </header>

        <div className={dropped ? "stale" : "panel__body"}>
          {slot.data === null && !slot.says ? <Skeleton lines={lines} label={t("ui.loading")} /> : null}

          {slot.data === null && slot.says ? (
            <div className="rowalarm rowalarm--red" data-trouble="">
              <span>{slot.says}</span>
              {slot.attempts === 1 ? (
                <p>
                  <button type="button" className="btn" onClick={() => again(retry)}>
                    {t("ui.retry")}
                  </button>
                </p>
              ) : null}
            </div>
          ) : null}

          {slot.data !== null ? <Drawn data={slot.data} draw={children} /> : null}
        </div>
      </section>
    </PanelBoundary>
  );
}

/** The render prop, called from a component of its own so that it runs BELOW the boundary. Called inline in
 *  Panel it ran in Panel's own render, which the boundary cannot catch, and one bad field took the page down. */
function Drawn<T>({ data, draw }: { data: T; draw: (data: T) => ReactNode }) {
  return <>{draw(data)}</>;
}

/** A skeleton the size of what replaces it. Nothing animates width, height, top or margin. */
export function Skeleton({ lines, label }: { lines: number; label: string }) {
  return (
    <div className="skel" role="status">
      <span className="vh">{label}</span>
      {Array.from({ length: lines }, (_, i) => (
        <span key={i} className="skel__bar" style={{ width: `${100 - i * 7}%` }} aria-hidden="true" />
      ))}
    </div>
  );
}
